import React, {useEffect, useRef} from 'react';

import { useSelector } from 'react-redux';

import Header from './Header';
import ScrollGoods from './../../Pages/Shop/ScrollGoods';
import SliderPanel from './../../Components/Menus/SliderPanel';
import CircularProgress from '@mui/material/CircularProgress';

import { useShopAPI } from './../../API/Shop';
import './Css/StorefrontTemplates.css';
import {
    normalizeStorefrontTemplate,
    storefrontTemplateClassName
} from '../../utils/storefrontTemplate';

const loadBasket = () => import('./BasketRoute');
const loadCatalog = () => import('./../../Pages/Shop/Catalog');
const loadProfile = () => import('./ProfileRoute');
const loadInfo = () => import('./../../Pages/Shop/Info');

const Basket = React.lazy(loadBasket);
const Catalog = React.lazy(loadCatalog);
const Profile = React.lazy(loadProfile);
const Info = React.lazy(loadInfo);

const shopRouteFallback = (
    <div style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9998,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--tg-theme-bg-color)'
    }}>
        <CircularProgress size={30} />
    </div>
);

class ShopRouteErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError() {
        return { hasError: true };
    }

    componentDidCatch(error, info) {
        console.error('Shop route error', error, info);
    }

    render() {
        if (this.state.hasError) {
            return (
                <div style={{
                    position: 'fixed',
                    inset: 0,
                    zIndex: 10000,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 24,
                    background: 'var(--tg-theme-bg-color)',
                    color: 'var(--tg-theme-text-color)'
                }}>
                    <div style={{ textAlign: 'center', maxWidth: 320 }}>
                        <div style={{ marginBottom: 16 }}>Раздел не открылся.</div>
                        <button className="btn mainButton" onClick={this.props.onClose}>
                            Вернуться в каталог
                        </button>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

const ShopRouter = ({
        handleCloseBackdrop,
        handleOpenBackdrop,
        handleCloseBasketDialog,
        handleOpenBasketDialog,
        handleOpenLoadingGoods,
        handleCloseLoadingGoods,
        isLoadingGoods,
        hasCheckedGoods,
        handleChangeCategoryID,
        goodsParams,
        setGoodsParams,
        selectShop,
        setSelectShop,
        openBasket,
        openCatalog,
        setOpenCatalog,
        setParameter,
        openProfile,
        handleCloseProfileDialog,
        handleOpenProfileDialog,
        parameter,
        openGoodModal,
        setOpenGoodModal,
        viewType,
        setViewType,
        openInfoShop,
        setOpenInfoShop,
        keepBasketOpen,
        cartHydrationState
}) => {

    const UserSession = useSelector(state => state.user.user);
    const ShopSession = useSelector(state => state.shop.shop);
    const storefrontTemplate = normalizeStorefrontTemplate(ShopSession.settings.storefront_template);
    const storefrontTemplateClass = storefrontTemplateClassName(storefrontTemplate);
    const [isVisibleTopButton, setIsVisibleTopButton] = React.useState(false);

    const { getGoods } = useShopAPI();

    const scrollGoodsRef = useRef(null);

    const pages = ['shop', 'basket', 'catalog', 'account'];
    const [currentPage, setCurrentPage] = React.useState(() => openBasket ? 'basket' : 'shop');
    const [isVisibleHeader, setIsVisibleHeader] = React.useState(true);

    React.useLayoutEffect(() => {
        const backButton = window.Telegram?.WebApp?.BackButton;

        if (!backButton) {
            return undefined;
        }

        if (currentPage === 'shop') {
            backButton.hide();

            // Child dialogs remove their handlers during this render.
            // Hide once more afterwards so Telegram restores its close control.
            const frameID = window.requestAnimationFrame(() => backButton.hide());
            return () => window.cancelAnimationFrame(frameID);
        }

        if (currentPage === 'catalog' || currentPage === 'info') {
            const handleSectionBack = () => setCurrentPage('shop');
            backButton.show();
            backButton.onClick(handleSectionBack);

            return () => backButton.offClick(handleSectionBack);
        }

        return undefined;
    }, [currentPage]);

    const [loadingAppendGoods, setLoadingAppendGoods] = React.useState(false);
    const loadingAppendGoodsRef = React.useRef(loadingAppendGoods);

    loadingAppendGoodsRef.current = loadingAppendGoods;

    const [stopAppend, setStopAppend] = React.useState(false);

    const [catalogListIndex, setCatalogListIndex] = React.useState([]);

    const handleChangeCatalogID = (id) => {
        setGoodsParams.setCatalogID(id);
        setOpenCatalog(false);
        setCurrentPage('shop');
    }

    const handleChangeShopIDSearchGoods = (id, containerTag) => {
        setGoodsParams.setShopIDSearchGoods(id);
        setGoodsParams.setContainerTag(containerTag);
        setGoodsParams.setSelectShop(containerTag);
    }

    const HeaderParams = {
        panel_type: ShopSession.settings.panel_type,
        shopid: UserSession.shopid,
        handleChangeCategoryID: handleChangeCategoryID,
        goodsParams: goodsParams,
        setGoodsParams: setGoodsParams,
        handleOpenBackdrop: handleOpenBackdrop,
        catalogListIndex: catalogListIndex,
        scrollContainerRef: scrollGoodsRef,
        isVisible: isVisibleHeader,
        setIsVisible: setIsVisibleHeader,
        setCurrentPage: setCurrentPage
    }

    const ScrollGoodsParams = {
        panel_type: ShopSession.settings.panel_type,
        isLoadingGoods: isLoadingGoods,
        hasCheckedGoods: hasCheckedGoods,
        handleCloseLoadingGoods: handleCloseLoadingGoods,
        handleCloseBackdrop: handleCloseBackdrop,
        goodsParams: goodsParams,
        handleOpenBackdrop: handleOpenBackdrop,
        scrollContainerRef: scrollGoodsRef,
        isVisibleHeader: isVisibleHeader,
        loadingAppendGoods: loadingAppendGoods,
        setOpenGoodModal: setOpenGoodModal,
        handleChangeShopIDSearchGoods: handleChangeShopIDSearchGoods,
        currentPage: currentPage
    };

    const PanelParams = {
        TGMainButtonColor: ShopSession.settings.TGMainButtonColor,
        colorThemeButton: ShopSession.settings.colorThemeButton,
        pages: pages,
        currentPage: currentPage,
        setCurrentPage: setCurrentPage,
        isVisible: isVisibleHeader,
        setViewType: setViewType
    };

    React.useLayoutEffect(() => {

        if(currentPage === 'basket') {
            handleOpenBasketDialog();
        } else {
            handleCloseBasketDialog();
        }

        if(currentPage === 'catalog') {
            setOpenCatalog(true);
        } else {
            setOpenCatalog(false);
        }

        if(currentPage === 'info') {
            setOpenInfoShop(true);
        } else {
            setOpenInfoShop(false);
        }

        const profileClosed = sessionStorage.getItem('profileClosed');

        if((parameter === 'account' || viewType === 'account') && (profileClosed === undefined || profileClosed === null)) {
            handleOpenProfileDialog();
            setCurrentPage('account');
            handleCloseBackdrop();
            // window.Telegram.WebApp.BackButton.hide();
            setParameter(null);
        }

        if(currentPage === 'account') {
            handleOpenProfileDialog();
        } else {
            handleCloseProfileDialog();
        }

        if(parameter !== undefined && parameter !== null) {
            const match = parameter.match(/orderid(\d+)/);
            if (match) {
                handleOpenProfileDialog();
                setCurrentPage('account');
                handleCloseBackdrop();
                setParameter(null);
            }
        }

    }, [currentPage, parameter]);

    React.useEffect(() => {

        const handleScroll = () => {
            const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

            setIsVisibleTopButton(scrollTop > 600);
        };

        window.addEventListener('scroll', handleScroll);

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    const scrollToTop = () => {
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
    };

    React.useEffect(() => {
        const handleScroll = async () => {
            if(currentPage !== 'shop') {
                return;
            }

            const scrollTop = window.scrollY;
            const documentHeight = document.documentElement.scrollHeight;
            const windowHeight = window.innerHeight;

            if (documentHeight - (scrollTop + windowHeight) < 300 && !loadingAppendGoodsRef.current && !stopAppend && ShopSession.settings.show === undefined) {
                loadingAppendGoodsRef.current = true;
                setLoadingAppendGoods(true);

                var ParamsSearch = {
                    categoryID: goodsParams.categoryID,
                    offset: goodsParams.offsetSearchGoods + 20,
                    filter: goodsParams.filterSearchGoods,
                    searchText: goodsParams.textSearchGoods,
                    catalogID: goodsParams.catalogID,
                    shopid: goodsParams.shopIDSearchGoods,
                    append: true
                };

                const abortCtrl = new AbortController();
                try {
                    const dataAxiosGoods = await getGoods(ParamsSearch, abortCtrl, () => {});
                    const goodsData = Object.values(dataAxiosGoods?.goods || {});

                    if(goodsData.length > 19) {
                        setGoodsParams.setOffsetSearchGoods(goodsParams.offsetSearchGoods + 20);
                    } else {
                        setStopAppend(true);
                    }
                } finally {
                    loadingAppendGoodsRef.current = false;
                    setLoadingAppendGoods(false);
                }
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [currentPage, goodsParams, setGoodsParams, stopAppend]);

    useEffect(() => {
        setStopAppend(false);
    }, [
        goodsParams.categoryID,
        goodsParams.filterSearchGoods,
        goodsParams.textSearchGoods,
        goodsParams.catalogID,
        goodsParams.shopIDSearchGoods
    ]);

    // handleOpenLoadingGoods();

    return (
        <div
            className={`storefront-shell ${storefrontTemplateClass}`}
            data-storefront-template={storefrontTemplate}
            style={{ minHeight: "100vh" }}
        >
            <div
                className="storefront-catalog-surface"
                aria-hidden={currentPage !== 'shop'}
                style={{
                    visibility: currentPage === 'shop' ? 'visible' : 'hidden',
                    pointerEvents: currentPage === 'shop' ? 'auto' : 'none'
                }}
            >
                <Header {...HeaderParams} />
                <ScrollGoods {...ScrollGoodsParams} />
            </div>
            <SliderPanel {...PanelParams} />
            <ShopRouteErrorBoundary
                key={currentPage}
                onClose={() => {
                    handleCloseBasketDialog();
                    setOpenCatalog(false);
                    handleCloseProfileDialog();
                    setOpenInfoShop(false);
                    setCurrentPage('shop');
                }}
            >
                <React.Suspense fallback={shopRouteFallback}>
                    {openBasket && (
                        <Basket currentPage={currentPage} openGoodModal={openGoodModal} openBasket={openBasket}
                                handleOpenBasketDialog={handleOpenBasketDialog} handleCloseBasketDialog={handleCloseBasketDialog}
                                setCurrentPage={setCurrentPage} panel_type={ShopSession.settings.panel_type}
                                keepBasketOpen={keepBasketOpen} cartHydrationState={cartHydrationState}/>
                    )}
                    {openCatalog && (
                        <Catalog setCatalogListIndex={setCatalogListIndex} handleCloseCatalogDialog={() => setOpenCatalog(false)}
                                 openCatalog={openCatalog} selectCatalog={(id) => handleChangeCatalogID(id)}/>
                    )}
                    {openProfile && (
                        <Profile openProfile={openProfile} handleCloseProfileDialog={handleCloseProfileDialog}
                                 panel_type={ShopSession.settings.panel_type} setCurrentPage={setCurrentPage}
                                 currentPage={currentPage} setViewType={(view) => setViewType(view)} viewType={viewType} />
                    )}
                    {openInfoShop && (
                        <Info openInfoShop={openInfoShop} setOpenInfoShop={setOpenInfoShop} setCurrentPage={setCurrentPage} />
                    )}
                </React.Suspense>
            </ShopRouteErrorBoundary>

            <button
                onClick={scrollToTop}
                className="mainButton btn btn-lg"
                style={{
                    display: currentPage === 'shop' && isVisibleTopButton ? 'block' : 'none',
                    position: 'fixed',
                    bottom: '100px',
                    left: '20px',
                    zIndex: '999'
                }}
            >
                ↑
            </button>
        </div>
    );
};

export default ShopRouter;
